import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { WORKFLOW_STAGES, type WorkflowStageValue } from "@/lib/constants/testing-constants";

interface Sprint {
  id: string;
  sprint_number: string;
  sprint_name: string | null;
  description: string | null;
  current_stage: WorkflowStageValue;
  stage_status: "pending" | "in_progress" | "passed" | "blocked" | "completed" | "skipped" | "approved";
  project_id?: string | null;
  module?: string | null;
  start_date: string | null;
  end_date: string | null;
}

interface Project {
  id: string;
  projectName: string;
}

interface SprintFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sprint?: Sprint | null;
}

export function SprintFormDialog({ open, onClose, onSuccess, sprint }: SprintFormDialogProps) {
  const { toast } = useToast();
  const isEditing = !!sprint;

  const [formData, setFormData] = useState({
    sprint_number: "",
    sprint_name: "",
    description: "",
    current_stage: "analysis" as WorkflowStageValue,
    stage_status: "pending" as Sprint["stage_status"],
    project_id: "",
    module: "",
    start_date: "",
    end_date: "",
  });

  // Load projects for dropdown (ISSUE-2 fix)
  const { data: projects } = useQuery<Project[]>({
    queryKey: ["/api/control-centre/projects"],
    enabled: open,
  });

  // Reset form when dialog opens/closes or sprint changes
  useEffect(() => {
    if (open) {
      if (sprint) {
        setFormData({
          sprint_number: sprint.sprint_number,
          sprint_name: sprint.sprint_name || "",
          description: sprint.description || "",
          current_stage: sprint.current_stage,
          stage_status: sprint.stage_status,
          project_id: sprint.project_id || "",
          module: sprint.module || "",
          start_date: sprint.start_date || "",
          end_date: sprint.end_date || "",
        });
      } else {
        setFormData({
          sprint_number: "",
          sprint_name: "",
          description: "",
          current_stage: "analysis",
          stage_status: "pending",
          project_id: "",
          module: "",
          start_date: "",
          end_date: "",
        });
      }
    }
  }, [open, sprint]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("POST", "/api/project-manager/sprints", data);
      return res.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("PATCH", `/api/project-manager/sprints/${sprint?.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.sprint_number || !formData.sprint_name) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Sprint number and name are required.",
      });
      return;
    }

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]" data-testid="sprint-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Sprint" : "Create Sprint"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update sprint details and configuration."
              : "Create a new development sprint for tracking implementation progress."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Sprint Number */}
          <div className="space-y-2">
            <Label htmlFor="sprint_number">
              Sprint Number <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sprint_number"
              placeholder="SPRINT-1"
              value={formData.sprint_number}
              onChange={(e) => setFormData({ ...formData, sprint_number: e.target.value })}
              disabled={isEditing} // Can't change sprint number after creation
              data-testid="sprint-number-input"
            />
          </div>

          {/* Sprint Name */}
          <div className="space-y-2">
            <Label htmlFor="sprint_name">
              Sprint Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="sprint_name"
              placeholder="Client Manager Phase 1"
              value={formData.sprint_name}
              onChange={(e) => setFormData({ ...formData, sprint_name: e.target.value })}
              data-testid="sprint-name-input"
            />
          </div>

          {/* Project (ISSUE-2 FIX) */}
          <div className="space-y-2">
            <Label htmlFor="project_id">Project</Label>
            <Select
              value={formData.project_id || "none"}
              onValueChange={(value) => setFormData({ ...formData, project_id: value === "none" ? "" : value })}
            >
              <SelectTrigger id="project_id" data-testid="project-select">
                <SelectValue placeholder="Select a project (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.projectName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Module */}
          <div className="space-y-2">
            <Label htmlFor="module">Module</Label>
            <Select
              value={formData.module || "none"}
              onValueChange={(value) => setFormData({ ...formData, module: value === "none" ? "" : value })}
            >
              <SelectTrigger id="module" data-testid="module-select">
                <SelectValue placeholder="Select a module (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="Client Manager">Client Manager</SelectItem>
                <SelectItem value="Deal Builder">Deal Builder</SelectItem>
                <SelectItem value="Commission Manager">Commission Manager</SelectItem>
                <SelectItem value="Issues Manager">Issues Manager</SelectItem>
                <SelectItem value="Document Manager">Document Manager</SelectItem>
                <SelectItem value="Workflow Manager">Workflow Manager</SelectItem>
                <SelectItem value="Testing">Testing</SelectItem>
                <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                <SelectItem value="Security">Security</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Workflow Stage */}
          <div className="space-y-2">
            <Label htmlFor="current_stage">Workflow Stage</Label>
            <Select
              value={formData.current_stage}
              onValueChange={(value) => setFormData({ ...formData, current_stage: value as WorkflowStageValue })}
            >
              <SelectTrigger id="current_stage" data-testid="stage-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WORKFLOW_STAGES.map((stage) => (
                  <SelectItem key={stage.dbValue} value={stage.dbValue}>
                    {stage.number}. {stage.name}
                    {stage.gate && ` (${stage.gate})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {WORKFLOW_STAGES.find(s => s.dbValue === formData.current_stage)?.description}
            </p>
          </div>

          {/* Stage Status */}
          <div className="space-y-2">
            <Label htmlFor="stage_status">Stage Status</Label>
            <Select
              value={formData.stage_status}
              onValueChange={(value) => setFormData({ ...formData, stage_status: value as Sprint["stage_status"] })}
            >
              <SelectTrigger id="stage_status" data-testid="stage-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Sprint goals and scope..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="description-textarea"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="end-date-input"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="submit-sprint-button">
              {isLoading ? "Saving..." : isEditing ? "Update Sprint" : "Create Sprint"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
